import { Link } from "react-router-dom";
import { ArrowRight, HeartHandshake, Leaf, ShieldCheck } from "lucide-react";
import { PRACTICE_GROUPS } from "@/data/practice";
import Reveal from "./Reveal";
import SectionHeading from "./SectionHeading";

const ICONS = { spine: HeartHandshake, other: ShieldCheck, bodywork: Leaf };

export default function Practice() {
    return (
        <section id="practice" data-testid="practice-section" className="bg-cream py-20 md:py-32">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <SectionHeading
                    overline="Ancient Wisdom • Holistic Approach • Personalized Care"
                    title="An Ancient & Holistic Approach to Personalized Care"
                    testId="practice-heading"
                >
                    At Agastya Chiro and Ancient Marma Chikitsa Center, we follow an ancient and
                    holistic approach to personalized care. Our practice combines traditional Marma
                    Chikitsa knowledge with hands-on therapeutic techniques and wellness therapies,
                    with attention to each person's individual concerns, comfort and wellbeing.
                </SectionHeading>

                <div className="mt-14 grid gap-6 lg:grid-cols-3">
                    {PRACTICE_GROUPS.map((g, gi) => {
                        const Icon = ICONS[g.id];
                        return (
                            <Reveal key={g.id} delay={gi * 0.08}>
                                <div
                                    data-testid={`practice-group-${g.id}`}
                                    className="flex h-full flex-col rounded-3xl border border-sand bg-bone p-8 transition-[box-shadow,transform] duration-500 hover:-translate-y-1 hover:shadow-[0_20px_50px_-20px_rgba(28,35,33,0.25)]"
                                >
                                    <span className="inline-flex w-fit rounded-2xl bg-forest/10 p-3 text-forest">
                                        <Icon className="h-5 w-5" />
                                    </span>
                                    <h3 className="mt-5 font-serif text-2xl font-medium leading-snug text-ink">
                                        {g.title}
                                    </h3>
                                    <p className="mt-2.5 text-sm leading-relaxed text-moss">{g.short}</p>
                                    <ul className="mt-5 flex-1 space-y-2.5">
                                        {g.items.map((item) => (
                                            <li key={item.name} className="flex items-center gap-2.5 text-sm font-medium text-ink">
                                                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-gold" aria-hidden="true" />
                                                {item.name}
                                            </li>
                                        ))}
                                    </ul>
                                    <Link
                                        to={`/conditions-and-concerns#${g.id}`}
                                        data-testid={`practice-link-${g.id}`}
                                        className="group mt-6 inline-flex items-center gap-2 text-sm font-semibold text-forest transition-colors duration-300 hover:text-terracotta"
                                    >
                                        Learn more
                                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                                    </Link>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>

                <Reveal delay={0.2}>
                    <p className="mt-10 max-w-3xl text-xs leading-relaxed text-moss/80">
                        Our care is supportive and complementary: we do not diagnose, treat, cure or
                        prevent any disease, and nothing here replaces the care of a qualified medical
                        practitioner. For serious, persistent, sudden or worsening symptoms, please seek
                        appropriate medical evaluation first.
                    </p>
                </Reveal>
            </div>
        </section>
    );
}
